// A work day can be split at an empty gap as well as at an activity. That is
// the only way to separate a day of time entries alone from the day before it,
// when one of its entries ended close enough to join the two.

import { differenceInMinutes, parseISO } from 'date-fns'

export interface WorkPeriodContent {
  datetimeStart: string
  datetimeEnd?: string | null
}

export interface WorkPeriodGap {
  start: Date
  end: Date
}

/** A shorter gap is a pause inside the day, not a plausible place to split it. */
export const MIN_SPLIT_GAP_MINUTES = 15

/**
 * The gaps no activity, estimate or time entry covers, in time order.
 *
 * Mirrors the backend's split-at-gap rule: a zero-length time entry is a note,
 * not registered work, so it neither fills a gap nor bounds one.
 */
export const findWorkPeriodGaps = (
  content: readonly WorkPeriodContent[],
  minMinutes = MIN_SPLIT_GAP_MINUTES
): WorkPeriodGap[] => {
  const spans = content
    .flatMap(item =>
      item.datetimeEnd
        ? [
            {
              start: parseISO(item.datetimeStart),
              end: parseISO(item.datetimeEnd),
            },
          ]
        : []
    )
    .filter(span => span.end > span.start)
    .sort((a, b) => a.start.getTime() - b.start.getTime())

  const gaps: WorkPeriodGap[] = []
  let coveredUntil: Date | null = null
  for (const span of spans) {
    if (
      coveredUntil &&
      differenceInMinutes(span.start, coveredUntil) >= minMinutes
    ) {
      gaps.push({ start: coveredUntil, end: span.start })
    }
    if (!coveredUntil || span.end > coveredUntil) coveredUntil = span.end
  }
  return gaps
}
